<div class="revision-review" [class.variant-client]="variant === 'client'" [class.variant-business]="variant === 'business'" [class.is-record]="isRecord">
  @if (!isRecord) {
  <section class="review-section">
    @if (view.overview.collapseToSingleLine) {
      <div class="overview-row">
        <span class="overview-label">Contract value</span>
        <span class="overview-value">{{ taggedMoney(view.overview.afterContractValue) }}</span>
      </div>
    } @else {
      @if (view.overview.hasAcceptedAdjustments) {
        <div class="overview-block">
          <div class="overview-heading">Before this revision</div>
          <div class="overview-row">
            <span class="overview-label">Original contract</span>
            <span class="overview-value">{{ money(view.overview.originalContractValue) }}</span>
          </div>
          <div class="overview-row">
            <span class="overview-label">Accepted variations</span>
            <span class="overview-value" [class]="netChangeClass(view.overview.acceptedVariationsNet)">
              {{ signedMoney(view.overview.acceptedVariationsNet) }}
            </span>
          </div>
          <div class="overview-row">
            <span class="overview-label">Current contract value</span>
            <span class="overview-value">{{ taggedMoney(view.overview.beforeContractValue) }}</span>
          </div>
        </div>
      } @else {
        <div class="overview-row">
          <span class="overview-label">Contract value</span>
          <span class="overview-value">{{ taggedMoney(view.overview.beforeContractValue) }}</span>
        </div>
      }
      <div class="overview-block">
        <div class="overview-heading">Changes in this revision</div>
        @if (view.overview.noValueChange) {
          <div class="overview-row muted">No change to contract value</div>
        } @else {
          @for (line of view.overview.changeLines; track line.label) {
            <div class="overview-row">
              <span class="overview-label">{{ line.label }}</span>
              <span class="overview-value" [class]="netChangeClass(line.amount)">{{ signedMoney(line.amount) }}</span>
            </div>
          }
          <div class="overview-row">
            <span class="overview-label">Revised contract value</span>
            <span class="overview-value">{{ taggedMoney(view.overview.afterContractValue) }}</span>
          </div>
          <div class="overview-row">
            <span class="overview-label">Net change</span>
            <span class="overview-value" [class]="netChangeClass(view.overview.netChange)">
              {{ signedMoney(view.overview.netChange) }}
            </span>
          </div>
        }
      </div>
    }
  </section>
  }

  @if (!isRecord || datesChanged) {
  <section class="review-card">
    <div class="fields fields-dates">
      <div class="field">
        <span class="field-label">Start</span>
        <span class="field-value">{{ dateLabel(view.projectDates.startDate) }}</span>
        @if (view.projectDates.previousStartDate) {
          <span class="was-previously">was previously {{ dateLabel(view.projectDates.previousStartDate) }}</span>
        }
        @if (pushedNote(view.projectDates.startPushedDays); as note) {
          <span class="derived-note">{{ note }}</span>
        }
      </div>
      <div class="field">
        <span class="field-label">End</span>
        <span class="field-value">{{ dateLabel(view.projectDates.endDate) }}</span>
        @if (view.projectDates.previousEndDate) {
          <span class="was-previously">was previously {{ dateLabel(view.projectDates.previousEndDate) }}</span>
        }
        @if (pushedNote(view.projectDates.endPushedDays); as note) {
          <span class="derived-note">{{ note }}</span>
        }
      </div>
    </div>
  </section>
  }

  @if (view.milestones.length) {
  <section class="review-section">
    <div class="section-heading">
      <h3>{{ isRecord ? "Changes to the project" : "Milestones" }}</h3>
      @if (!isRecord) {
        <span class="muted">{{ milestoneCountLine }}</span>
      }
    </div>
    <div class="card-stack">
      @for (milestone of view.milestones; track milestone.id) {
        <article class="review-card" [class.card-new]="milestone.tag === 'new'" [class.card-deleted]="milestone.tag === 'deleted'">
          @if (milestone.tag) {
            <span class="state-tag" [attr.data-tag]="milestone.tag">{{ tagLabel(milestone.tag) }}</span>
          }
          <div class="card-name">{{ milestone.name }}</div>
          @if (milestone.previousName) {
            <div class="was-previously">was previously {{ milestone.previousName }}</div>
          }
          @if (milestone.description) {
            <p class="card-description">{{ milestone.description }}</p>
          }
          @if (milestone.previousDescription) {
            <div class="was-previously">was previously {{ milestone.previousDescription }}</div>
          }
          <div class="fields">
            <div class="field field-amount">
              <span class="field-label">Amount</span>
              <span class="field-value">{{ money(milestone.amount) }}</span>
            </div>
            <div class="field">
              <span class="field-label">Start</span>
              <span class="field-value">{{ dateLabel(milestone.startDate) }}</span>
              @if (milestone.previousStartDate) {
                <span class="was-previously">was previously {{ dateLabel(milestone.previousStartDate) }}</span>
              }
              @if (pushedNote(milestone.startPushedDays); as note) {
                <span class="derived-note">{{ note }}</span>
              }
            </div>
            <div class="field">
              <span class="field-label">End</span>
              <span class="field-value">{{ dateLabel(milestone.endDate) }}</span>
              @if (milestone.previousEndDate) {
                <span class="was-previously">was previously {{ dateLabel(milestone.previousEndDate) }}</span>
              }
              @if (pushedNote(milestone.endPushedDays); as note) {
                <span class="derived-note">{{ note }}</span>
              }
            </div>
          </div>
          @if (milestone.deletionSentence) {
            <p class="deletion-copy">{{ variant === "client" ? "This milestone will be deleted from the project. The work will not be carried out and you will not be charged for it." : milestone.deletionSentence }}</p>
            <div class="deletion-strip amount-decrease">Contract value reduces by {{ money(milestone.deletionStripAmount) }}</div>
          }
          @if (milestone.submilestones.length) {
            <div class="submilestone-block">
              @for (sub of milestone.submilestones; track sub.id) {
                <div class="submilestone-row" [class.is-deleted]="sub.tag === 'deleted'">
                  @if (sub.tag) {
                    <span class="state-tag inline" [attr.data-tag]="sub.tag">{{ tagLabel(sub.tag) }}</span>
                  }
                  <span class="sub-name">{{ sub.name }}</span>
                  <span class="sub-amount">{{ money(sub.amount) }}</span>
                </div>
              }
            </div>
          }
        </article>
      }
    </div>
  </section>
  }

  @if (view.adjustments.length) {
    <section class="review-section">
      <div class="section-heading">
          <h3>Variations</h3>
      </div>
      <div class="card-stack">
        @for (adjustment of view.adjustments; track adjustment.id) {
          <article class="review-card" [class.card-new]="adjustment.tag === 'new'">
            @if (adjustment.tag) {
              <span class="state-tag" [attr.data-tag]="adjustment.tag">{{ tagLabel(adjustment.tag) }}</span>
            }
            <div class="card-name">{{ adjustment.label }}{{ adjustment.displayCode ? " " + adjustment.displayCode : "" }}</div>
            @if (adjustment.description) {
              <p class="card-description">{{ adjustment.description }}</p>
            }
            <div class="fields fields-dates">
              <div class="field">
                <span class="field-label">Amount</span>
                <span class="field-value" [class]="netChangeClass(adjustment.signedAmount)">{{ adjustment.amount ? money(adjustment.signedAmount) : "—" }}</span>
              </div>
              <div class="field">
                <span class="field-label">Status</span>
                <span class="field-value">{{ moneyStateLabel(adjustment.moneyLabel) }}</span>
              </div>
            </div>
          </article>
        }
      </div>
    </section>
  }

  @if (view.invoices.length) {
  <section class="review-section">
    <div class="section-heading">
      <h3>Payment schedule</h3>
    </div>
    <div class="card-stack">
      @for (invoice of view.invoices; track invoice.id) {
        <article class="review-card invoice-card" [class.card-new]="invoice.tag === 'new'" [class.card-deleted]="invoice.tag === 'deleted'">
          @if (invoice.tag) {
            <span class="state-tag" [attr.data-tag]="invoice.tag">{{ tagLabel(invoice.tag) }}</span>
          }
          <div class="card-name">{{ invoice.issuedLabel }}</div>
          <div class="invoice-due">
            <span class="field-label">Due</span>
            <span class="field-value nowrap">{{ dateLabel(invoice.dueDate) }}</span>
            @if (invoice.previousDueDate) {
              <span class="was-previously">was previously {{ dateLabel(invoice.previousDueDate) }}</span>
            }
          </div>
          @for (line of invoice.lines; track line.id) {
            <div class="invoice-line">
              <span>{{ line.name }}</span>
              <span class="nowrap">{{ money(line.amount) }}</span>
            </div>
          }
        </article>
      }
    </div>
    @if (!isRecord) {
      <div class="overview-row remaining">
        <span class="overview-label">Remaining to pay</span>
        <span class="overview-value">{{ taggedMoney(view.remainingToPay) }}</span>
      </div>
    }
  </section>
  }
</div>
